import { Alert, Button, Group, Modal, Stack, Text, TextInput } from '@mantine/core';
import { useForm } from '@mantine/form';
import { notifications } from '@mantine/notifications';
import { useQueryClient } from '@tanstack/react-query';
import { zod4Resolver } from 'mantine-form-zod-resolver';
import type { ReactNode } from 'react';
import { z } from 'zod';
import { useAuth } from '../auth/useAuth.js';
import { useT } from '../i18n/useT.js';
import type { MessageKey } from '../i18n/da.js';
import { useCreateCompany } from '../api/hooks.js';
import { ErrorMessage } from './ErrorMessage.js';

/**
 * Add a company, from the switcher's own dropdown.
 *
 * Placed there rather than in a settings list because that is where a person
 * already thinks about which companies they have — and because the switcher
 * already knows their role in each, which is what decides whether the control
 * may be shown at all. A settings page would have to learn that separately.
 *
 * The creator becomes the owner in the same transaction. A company with no
 * owner would be unreachable: every /v1 route needs a company token, and tokens
 * are issued only to members.
 */

function schemaFor(t: (key: MessageKey) => string) {
  return z.object({
    name: z.string().trim().min(1, t('company.add.nameRequired')).max(255),
    cvr: z
      .string()
      .trim()
      .refine((value) => value === '' || /^[0-9]{8}$/u.test(value), {
        message: t('company.add.cvrInvalid'),
      }),
  });
}

type Values = z.infer<ReturnType<typeof schemaFor>>;

export interface AddCompanyModalProps {
  readonly opened: boolean;
  readonly onClose: () => void;
}

export function AddCompanyModal({ opened, onClose }: AddCompanyModalProps): ReactNode {
  const t = useT();
  const { refresh, selectCompany } = useAuth();
  const queryClient = useQueryClient();
  const create = useCreateCompany();

  const form = useForm<Values>({
    mode: 'uncontrolled',
    initialValues: { name: '', cvr: '' },
    validate: zod4Resolver(schemaFor(t)),
  });

  function close(): void {
    form.reset();
    create.reset();
    onClose();
  }

  function handleSubmit(values: Values): void {
    create.mutate(
      {
        name: values.name.trim(),
        // Omitted rather than sent empty: the API's CVR pattern is 8 digits and
        // would refuse '', and "not recorded yet" is a real state -- a company
        // can be created before its registration comes through.
        ...(values.cvr.trim() === '' ? {} : { cvr: values.cvr.trim() }),
      },
      {
        onSuccess: async (company) => {
          notifications.show({
            color: 'green',
            title: t('company.add.created'),
            message: t('company.add.createdDetail', { name: company.name }),
          });
          close();
          // Pick up the new membership, then switch to it: creating a company
          // and staying in the old one is not what anyone means by the button.
          await refresh();
          await selectCompany(company.id);
          // Everything cached was read under the previous company's token.
          await queryClient.resetQueries();
        },
      },
    );
  }

  return (
    <Modal opened={opened} onClose={close} title={t('company.add.title')} size="md">
      <form noValidate onSubmit={form.onSubmit(handleSubmit)}>
        <Stack gap="md">
          <TextInput
            label={t('company.add.name')}
            withAsterisk
            placeholder="Sipotek ApS"
            key={form.key('name')}
            {...form.getInputProps('name')}
          />

          <TextInput
            label={t('company.add.cvr')}
            placeholder={t('company.add.cvrPlaceholder')}
            description={t('company.add.cvrHelp')}
            key={form.key('cvr')}
            {...form.getInputProps('cvr')}
          />

          <Alert color="blue" variant="light" title={t('company.add.warningTitle')}>
            <Text size="sm">{t('company.add.warning')}</Text>
          </Alert>

          {create.isError && (
            <ErrorMessage error={create.error} title={t('company.add.failed')} />
          )}

          <Group justify="flex-end">
            <Button variant="default" onClick={close}>
              {t('common.cancel')}
            </Button>
            <Button type="submit" loading={create.isPending}>
              {t('common.create')}
            </Button>
          </Group>
        </Stack>
      </form>
    </Modal>
  );
}
